const EMAIL_MD5_LABEL = 'Email/md5';

export const UNIVERSE_IDENTITY_HEADERS = new Set([
  'md5',
  'email',
  'email_md5',
  'md5_lower',
  'email_md5_lower',
  'hash',
]);

const looksLikeMd5 = (s) => /^[a-fA-F0-9]{32}$/.test(String(s ?? '').trim());
const looksLikeEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s ?? '').trim());

export const firstCsvLine = (text) => {
  const cleaned = String(text ?? '').replace(/^\uFEFF/, '');
  const nl = cleaned.search(/\r|\n/);
  return (nl === -1 ? cleaned : cleaned.slice(0, nl)).trimEnd();
};

/** Split one CSV line, keeping empty middle cells so 1-based indexes stay aligned. */
export const parseCsvRow = (line) => {
  const raw = String(line ?? '').replace(/^\uFEFF/, '');
  if (!raw) return [];
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < raw.length; i += 1) {
    const ch = raw[i];
    if (ch === '"') {
      if (inQuotes && raw[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  while (out.length > 1 && out[out.length - 1] === '') out.pop();
  return out;
};

/**
 * True when the first CSV row should be treated as column names.
 * Matches Databricks validate_universe, but does not treat a first-cell MD5 or
 * email address as a header (even if email format is selected).
 */
export const universeCsvUsesNamedHeader = (
  columnNames,
  { emailFormat = 'md5', propertyNames = [] } = {}
) => {
  const cols = [...(columnNames || [])].map((c) => String(c ?? '').trim());
  const lowers = cols.map((c) => c.toLowerCase()).filter(Boolean);
  if (!lowers.length) return false;
  if (UNIVERSE_IDENTITY_HEADERS.has(lowers[0])) return true;
  if (looksLikeMd5(cols[0]) || looksLikeEmail(cols[0])) return false;
  if (String(emailFormat || '').trim().toLowerCase() === 'email') return true;
  const props = new Set(
    [...(propertyNames || [])].map((n) => String(n).trim().toLowerCase()).filter(Boolean)
  );
  return Boolean(props.size && lowers.some((c) => props.has(c)));
};

export const getUniverseFileColumnChoices = (columnNames, { hasHeader = false } = {}) => {
  const cols = [...(columnNames || [])];
  if (!cols.length) {
    return Array.from({ length: 40 }, (_, i) => ({
      value: String(i + 1),
      label: String(i + 1),
    }));
  }
  return cols.slice(0, 40).map((name, i) => {
    const value = String(i + 1);
    const trimmed = String(name ?? '').trim();
    return { value, label: hasHeader ? trimmed || `Column ${i + 1}` : value };
  });
};

export const defaultUniverseColumnMappings = ({
  columnNames = [],
  hasHeader = false,
  properties = [],
} = {}) => {
  let emailMd5Column = '1';
  if (hasHeader) {
    const idx = columnNames.findIndex((c) =>
      UNIVERSE_IDENTITY_HEADERS.has(String(c ?? '').trim().toLowerCase())
    );
    if (idx >= 0) emailMd5Column = String(idx + 1);
  }
  const propertyColumns = {};
  properties.forEach((p, i) => {
    if (!p?.name) return;
    if (hasHeader) {
      const idx = columnNames.findIndex(
        (c) => String(c ?? '').trim().toLowerCase() === String(p.name).trim().toLowerCase()
      );
      propertyColumns[p.name] = idx >= 0 ? String(idx + 1) : String(i + 2);
    } else {
      propertyColumns[p.name] = String(i + 2);
    }
  });
  return { emailMd5Column, propertyColumns };
};

const describeColumn = (col, columnLabels) => {
  const name = columnLabels?.[col];
  if (name && String(name).trim() && String(name).trim() !== String(col)) {
    return `"${String(name).trim()}" (column ${col})`;
  }
  return `column ${col}`;
};

const joinFieldNames = (names) => {
  if (names.length <= 1) return names[0] || '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};

/**
 * If Email/md5 or included org properties share a 1-based column number, return a
 * descriptive error for the upload dialog; otherwise null.
 */
export const getUniverseDuplicateColumnError = ({
  emailMd5Column,
  properties = [],
  propertyColumns = {},
  propertyExcluded = {},
  columnLabels = {},
} = {}) => {
  const labelsByColumn = new Map();
  const add = (col, label) => {
    const key = String(col ?? '').trim();
    if (!key || !label) return;
    const list = labelsByColumn.get(key) || [];
    list.push(label);
    labelsByColumn.set(key, list);
  };

  add(emailMd5Column, EMAIL_MD5_LABEL);
  properties.forEach((p) => {
    if (!p?.name || propertyExcluded[p.name]) return;
    add(propertyColumns[p.name], p.display_name || p.name);
  });

  const conflicts = [...labelsByColumn.entries()].filter(([, labels]) => labels.length > 1);
  if (conflicts.length === 0) return null;

  const details = conflicts.map(
    ([col, labels]) => `${joinFieldNames(labels)} are mapped to ${describeColumn(col, columnLabels)}`
  );
  if (details.length === 1) {
    return `${details[0]}. Each field must use a different column.`;
  }
  return (
    `Some fields are mapped to the same column:\n${details.map((d) => `• ${d}.`).join('\n')}\n` +
    'Each field must use a different column.'
  );
};
